import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  CheckCircle,
  Mail,
  Trophy,
  Trash2,
  Star,
  Calendar,
} from "lucide-react";

const Shortlisted = () => {
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "hrShortlisted"),
      where("hrId", "==", user.id)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by shortlisted date (newest first)
      results.sort((a, b) => {
        const dateA = a.shortlistedAt?.toDate?.() || new Date(0);
        const dateB = b.shortlistedAt?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      setCandidates(results);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, db]);

  const handleRemove = async (candidateId, candidateName) => {
    if (!confirm(`Remove ${candidateName} from shortlist?`)) return;

    try {
      await deleteDoc(doc(db, "hrShortlisted", candidateId));
      alert(`${candidateName} has been removed from shortlist.`);
    } catch (error) {
      console.error("Error removing candidate:", error);
      alert("Failed to remove candidate");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getRecommendationBadge = (recommendation) => {
    switch (recommendation) {
      case "STRONG RECOMMEND":
        return (
          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full inline-flex items-center">
            <Star className="w-3 h-3 mr-1" />
            Strong Recommend
          </span>
        );
      case "RECOMMEND":
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
            Recommend
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">
            {recommendation}
          </span>
        );
    }
  };

  if (loading) {
    return <div className="text-center py-10">Loading shortlisted candidates...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <CheckCircle className="w-8 h-8 mr-3 text-green-600" />
            Shortlisted Candidates
          </h1>
          <p className="text-gray-600 mt-2">
            {candidates.length} candidate{candidates.length !== 1 ? "s" : ""} shortlisted
          </p>
        </div>
      </div>

      {candidates.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
          <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">No shortlisted candidates yet.</p>
          <p className="text-gray-500 text-sm mt-2">
            Shortlist candidates from test results to see them here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Test
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    AI Recommendation
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Shortlisted On
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {candidates.map((candidate) => (
                  <tr key={candidate.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{candidate.candidateName}</p>
                        <p className="text-sm text-gray-600 flex items-center mt-1">
                          <Mail className="w-3 h-3 mr-1" />
                          {candidate.candidateEmail}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">{candidate.testTitle}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <span className="font-bold text-gray-900">
                          {candidate.totalScore?.toFixed(1) || 0}/100
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getRecommendationBadge(candidate.recommendationForHiring)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(candidate.shortlistedAt)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleRemove(candidate.id, candidate.candidateName)}
                        className="flex items-center text-red-600 hover:text-red-800 text-sm font-medium transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shortlisted;
